import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(params.get("next") || "/app");
    } catch (err) {
      setError(errMsg(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div className="flex flex-col justify-center px-6 md:px-16 lg:px-24 py-12">
        <Logo />
        <h1 className="mt-12 font-serif text-3xl md:text-4xl font-bold tracking-tight" data-testid="login-title">Welcome back</h1>
        <p className="mt-2 text-sm text-muted-foreground">Log in to continue your revision.</p>
        <form onSubmit={submit} className="mt-8 space-y-5 max-w-md">
          {error && <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm px-4 py-3 rounded-md" data-testid="login-error">{error}</div>}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="login-email-input" type="email" required autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="password">Password</Label>
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-primary hover:underline">Forgot password?</Link>
            </div>
            <Input id="password" data-testid="login-password-input" type="password" required autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </div>
          <Button type="submit" data-testid="login-submit-btn" disabled={loading} className="w-full">
            {loading ? "Logging in…" : "Log in"}
          </Button>
          <p className="text-sm text-muted-foreground">
            No account yet?{" "}
            <Link to="/register" data-testid="goto-register-link" className="text-primary font-medium hover:underline">Sign up</Link>
          </p>
        </form>
      </div>
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Physician associate in medical uniform with stethoscope in a hospital corridor"
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/60" />
        <div className="absolute bottom-16 left-16 right-16 text-primary-foreground">
          <p className="font-serif text-2xl font-bold leading-snug">"The question bank I wish had existed for PA school."</p>
          <p className="mt-3 text-sm text-primary-foreground/70">PA-specific SBAs. Explanations that teach. Progress that motivates.</p>
        </div>
      </div>
    </div>
  );
}
